import apiClient from './client';

// Cluster interfaces
export interface Cluster {
  id: number;
  code: string;
  name: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
  metric_count?: number;
  subdimension_count?: number;
}

export interface CreateClusterRequest {
  code: string;
  name: string;
  description?: string;
}

export interface UpdateClusterRequest {
  name?: string;
  description?: string;
}

// Sub-dimension interfaces
export interface SubDimension {
  id: number;
  clusterId: number;
  name: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
  cluster?: Cluster;
  metric_count?: number;
}

export interface CreateSubDimensionRequest {
  clusterId: number;
  name: string;
  description?: string;
}

export interface UpdateSubDimensionRequest {
  name?: string;
  description?: string;
}

export interface LinkSubDimensionRequest {
  clusterId: number;
}

// Metric interfaces
export interface Metric {
  id: number;
  metricId: string;
  name: string;
  searchName?: string;
  definition: string;
  unit: string;
  metricType?: string;
  isAggregatable: boolean;
  impactDirection?: string;
  aggregationMethod?: string;
  updateFrequency?: string;
  cluster?: Cluster;
  subDimension?: SubDimension;
  searchConfig?: SearchConfig;
  createdAt?: string;
  updatedAt?: string;
  valueCount?: number;
  baselineCount?: number;
  scenarioCount?: number;
  lastDataUpdate?: string;
  countries?: string[];
  dataQuality?: string;
}

export interface SearchConfig {
  tier1Queries?: string[];
  tier2Queries?: string[];
  tier3Queries?: string[];
  queryTemplateVariables?: {
    country?: string;
    year?: string;
    country_domain?: string;
    metric_name?: string;
  };
}

export interface CreateMetricRequest {
  cluster: string;
  subDimensionId: string;
  name: string;
  searchName: string;
  definition: string;
  unit: string;
  metricType?: string;
  isAggregatable: boolean;
  impactDirection?: string;
  aggregationMethod?: string;
  updateFrequency?: string;
  searchConfig?: SearchConfig;
}

export interface UpdateMetricRequest {
  cluster?: string;
  subDimensionId?: string;
  name?: string;
  searchName?: string;
  definition?: string;
  unit?: string;
  metricType?: string;
  isAggregatable?: boolean;
  impactDirection?: string;
  aggregationMethod?: string;
  updateFrequency?: string;
  searchConfig?: SearchConfig;
}

export interface MetricValue {
  id?: number;
  metricId: string;
  countryCode: string;
  year: number;
  value: number;
  dataQuality: string;
  confidenceScore?: number;
  frequency?: string;
  investorLens?: string;
  isBaseline: boolean;
  isScenario: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface CreateMetricValueRequest {
  metricId: string;
  countryCode: string;
  year: number;
  value: number;
  dataQuality: string;
  confidenceScore?: number;
  frequency?: string;
  investorLens?: string;
  isBaseline?: boolean;
  isScenario?: boolean;
}

export interface MetricFilters {
  cluster?: string;
  country?: string;
  dataQuality?: string;
  search?: string;
  classification?: string;
  isImpactMetric?: boolean;
  hasBaseline?: boolean;
  hasScenario?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

export interface MetricStats {
  totalMetrics: number;
  metricsByCluster: { [key: string]: number };
  metricsByUnit: { [key: string]: number };
  dataQualityStats: { [key: string]: number };
  coverageStats: { [key: string]: any };
}

export interface MetricAggregation {
  metricId: string;
  countryCode?: string;
  avgValue: number;
  minValue: number;
  maxValue: number;
  latestValue: number;
  latestYear: number;
  dataPoints: number;
  trend?: string;
  growthRate?: number;
}

// Unit and Type Options (constants)
export const METRIC_UNITS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'people_count', label: 'People Count' },
  { value: 'currency_value', label: 'Currency Value' },
  { value: 'time_duration', label: 'Time Duration' },
  { value: 'ratio', label: 'Ratio' },
];

export const METRIC_TYPES = [
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' },
  { value: 'people_count', label: 'People Count' },
  { value: 'time_duration', label: 'Time Duration' },
  { value: 'ratio', label: 'Ratio' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'rate', label: 'Rate' },
  { value: 'score', label: 'Score' },
];

export const AGGREGATION_METHODS = [
  { value: 'sum', label: 'Sum' },
  { value: 'average', label: 'Average' },
  { value: 'maximum', label: 'Maximum' },
  { value: 'minimum', label: 'Minimum' },
  { value: 'none', label: 'None' },
];

export const UPDATE_FREQUENCIES = [
  { value: 'annual', label: 'Annual' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi-annual', label: 'Semi-Annual' },
];

export const IMPACT_DIRECTIONS = [
  { value: 'positive', label: 'Positive (Higher is better)' },
  { value: 'negative', label: 'Negative (Lower is better)' },
];

export const DATA_QUALITY_LEVELS = [
  { value: 'hard', label: 'Hard', icon: '✓', color: 'green' },
  { value: 'soft', label: 'Soft', icon: '○', color: 'yellow' },
  { value: 'poor', label: 'Poor', icon: '✗', color: 'red' },
];

class MetricsApi {
  // ============ CLUSTER OPERATIONS ============
  
  async getClusters(): Promise<Cluster[]> {
    try {
      const response = await apiClient.getRaw<Cluster[]>('/metrics/clusters');
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch clusters:', error);
      throw error;
    }
  }

  async createCluster(data: CreateClusterRequest): Promise<Cluster> {
    try {
      return await apiClient.post<Cluster>('/metrics/clusters', data);
    } catch (error) {
      console.error('Failed to create cluster:', error);
      throw error;
    }
  }

  async updateCluster(id: number, data: UpdateClusterRequest): Promise<Cluster> {
    try {
      return await apiClient.put<Cluster>(`/metrics/clusters/${id}`, data);
    } catch (error) {
      console.error('Failed to update cluster:', error);
      throw error;
    }
  }

  async deleteCluster(id: number): Promise<void> {
    try {
      await apiClient.delete(`/metrics/clusters/${id}`);
    } catch (error) {
      console.error('Failed to delete cluster:', error);
      throw error;
    }
  }

  // ============ SUB-DIMENSION OPERATIONS ============
  
  async getSubDimensions(): Promise<SubDimension[]> {
    try {
      const response = await apiClient.getRaw<SubDimension[]>('/metrics/subdimensions');
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch sub-dimensions:', error);
      throw error;
    }
  }

  async getSubDimensionsByCluster(clusterId: string): Promise<SubDimension[]> {
    try {
      const response = await apiClient.getRaw<SubDimension[]>(`/metrics/clusters/${clusterId}/subdimensions`);
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch sub-dimensions by cluster:', error);
      throw error;
    }
  }

  async createSubDimension(data: CreateSubDimensionRequest): Promise<SubDimension> {
    try {
      return await apiClient.post<SubDimension>('/metrics/subdimensions', data);
    } catch (error) {
      console.error('Failed to create sub-dimension:', error);
      throw error;
    }
  }

  async updateSubDimension(id: number, data: UpdateSubDimensionRequest): Promise<SubDimension> {
    try {
      return await apiClient.put<SubDimension>(`/metrics/subdimensions/${id}`, data);
    } catch (error) {
      console.error('Failed to update sub-dimension:', error);
      throw error;
    }
  }

  async deleteSubDimension(id: number): Promise<void> {
    try {
      await apiClient.delete(`/metrics/subdimensions/${id}`);
    } catch (error) {
      console.error('Failed to delete sub-dimension:', error);
      throw error;
    }
  }

  async linkSubDimensionToCluster(subDimensionId: number, clusterId: number): Promise<void> {
    try {
      await apiClient.put(`/metrics/subdimensions/${subDimensionId}/link`, { clusterId });
    } catch (error) {
      console.error('Failed to link sub-dimension to cluster:', error);
      throw error;
    }
  }

  async unlinkSubDimensionFromCluster(subDimensionId: number, clusterId: number): Promise<void> {
    try {
      await apiClient.delete(`/metrics/subdimensions/${subDimensionId}/clusters/${clusterId}`);
    } catch (error) {
      console.error('Failed to unlink sub-dimension from cluster:', error);
      throw error;
    }
  }

  // ============ METRIC OPERATIONS ============
  
  async getMetrics(filters?: MetricFilters): Promise<Metric[]> {
    try {
      // Ensure default pagination parameters
      const params = {
        page: 1,
        limit: 100,
        ...filters,
      };
      const response = await apiClient.getRaw<Metric[]>('/metrics', {
        params,
      });
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      throw error;
    }
  }

  async getMetricById(id: string): Promise<Metric> {
    try {
      const response = await apiClient.getRaw<Metric>(`/metrics/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch metric by ID:', error);
      throw error;
    }
  }

  async createMetric(data: CreateMetricRequest): Promise<Metric> {
    try {
      return await apiClient.post<Metric>('/metrics', data);
    } catch (error) {
      console.error('Failed to create metric:', error);
      throw error;
    }
  }

  async updateMetric(id: string, data: UpdateMetricRequest): Promise<Metric> {
    try {
      return await apiClient.put<Metric>(`/metrics/${id}`, data);
    } catch (error) {
      console.error('Failed to update metric:', error);
      throw error;
    }
  }

  async deleteMetricData(id: string): Promise<void> {
    try {
      await apiClient.delete(`/metrics/${id}/data`);
    } catch (error) {
      console.error('Failed to delete metric data:', error);
      throw error;
    }
  }

  // ============ METRIC VALUE OPERATIONS ============
  
  async getMetricValues(metricId: string, filters?: any): Promise<MetricValue[]> {
    try {
      const response = await apiClient.getRaw<MetricValue[]>(`/metrics/${metricId}/values`, {
        params: filters,
      });
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch metric values:', error);
      throw error;
    }
  }

  async createMetricValue(metricId: string, data: CreateMetricValueRequest): Promise<MetricValue> {
    try {
      return await apiClient.post<MetricValue>(`/metrics/${metricId}/values`, data);
    } catch (error) {
      console.error('Failed to create metric value:', error);
      throw error;
    }
  }

  async getAllCountryMetricValues(metricId: string): Promise<any> {
    try {
      const response = await apiClient.getRaw(`/metrics/${metricId}/countries/values`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch country metric values:', error);
      throw error;
    }
  }

  // ============ STATISTICS & AGGREGATIONS ============
  
  async getMetricStats(): Promise<MetricStats> {
    try {
      const response = await apiClient.getRaw<MetricStats>('/metrics/stats');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch metric stats:', error);
      throw error;
    }
  }

  async getMetricAggregations(filters?: any): Promise<MetricAggregation[]> {
    try {
      const response = await apiClient.getRaw<MetricAggregation[]>('/metrics/aggregations', {
        params: filters,
      });
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch metric aggregations:', error);
      throw error;
    }
  }

  async getMasterMetricWithImpactInfo(metricId: string): Promise<any> {
    try {
      const response = await apiClient.getRaw(`/metrics/${metricId}/impact-info`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch master metric with impact info:', error);
      throw error;
    }
  }

  // ============ HELPER METHODS ============
  
  validateSearchQueries(queries: string[]): string[] {
    const errors: string[] = [];
    
    queries.forEach((query, index) => {
      if (!query || query.trim().length === 0) {
        errors.push(`Query ${index + 1} cannot be empty`);
      }
      
      // Check for balanced parentheses
      const openParens = (query.match(/\(/g) || []).length;
      const closeParens = (query.match(/\)/g) || []).length;
      if (openParens !== closeParens) {
        errors.push(`Query ${index + 1} has unbalanced parentheses`);
      }
      
      // Check for valid template variables
      const validVariables = ['{country}', '{year}', '{country_domain}', '{metric_name}'];
      const foundVariables = query.match(/\{[^}]+\}/g) || [];
      foundVariables.forEach(variable => {
        if (!validVariables.includes(variable)) {
          errors.push(`Query ${index + 1} contains invalid variable: ${variable}`);
        }
      });
    });
    
    return errors;
  }

  formatMetricId(name: string, cluster: string): string {
    // Generate a metric ID from name and cluster
    const cleanName = name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const cleanCluster = cluster.toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return `${cleanCluster}_${cleanName}`;
  }

  getDataQualityIcon(quality: string): { icon: string; color: string } {
    const qualityLevel = DATA_QUALITY_LEVELS.find(level => level.value === quality?.toLowerCase());
    return qualityLevel ? { icon: qualityLevel.icon, color: qualityLevel.color } : { icon: '?', color: 'gray' };
  }
}

const metricsApi = new MetricsApi();
export default metricsApi;